if (!customElements.get('bm-landing-ring-process')) {
  class BmLandingRingProcess extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-bm-lrp-track]');
      this.pin = this.querySelector('[data-bm-lrp-pin]');
      this.steps = this.querySelectorAll('[data-bm-lrp-step]');
      this.progressFill = this.querySelector('[data-bm-lrp-progress-fill]');
      this.mProgressFill = this.querySelector('[data-bm-lrp-mprogress-fill]');
      this.liveRegion = this.querySelector('[data-bm-lrp-live]');

      this.stepCount = this.steps.length;
      if (!this.track || !this.pin || this.stepCount === 0) return;

      this.railHeight = parseFloat(this.dataset.railHeight) || 393;
      this.segmentVh = parseFloat(this.dataset.segmentVh) || 100;
      this.crossfadeRatio = this.parseCssNumber('--bm-lrp-crossfade-ratio', 0.18);
      this.activeIndex = 0;
      this.ticking = false;

      this.mqMobile = window.matchMedia('(max-width: 989px)');
      this.mqReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.updateFromScroll();
        });
      };

      this.onMediaChange = () => this.applyMode();
      this.onResize = () => {
        if (!this.stackedMode) this.updateFromScroll();
      };

      this.applyMode();

      this.mqMobile.addEventListener('change', this.onMediaChange);
      this.mqReducedMotion.addEventListener('change', this.onMediaChange);
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.mqMobile) this.mqMobile.removeEventListener('change', this.onMediaChange);
      if (this.mqReducedMotion) this.mqReducedMotion.removeEventListener('change', this.onMediaChange);
    }

    parseCssNumber(variableName, fallback) {
      const raw = getComputedStyle(this).getPropertyValue(variableName).trim();
      const parsed = parseFloat(raw);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    getTrackMetrics() {
      const trackRect = this.track.getBoundingClientRect();
      const trackTop = trackRect.top + window.scrollY;
      const trackHeight = this.track.offsetHeight;
      const trackBottom = trackTop + trackHeight;

      return { trackTop, trackHeight, trackBottom };
    }

    applyMode() {
      // Mobile and desktop share the pinned scroll-storytelling mechanic (only the
      // layout differs, via CSS). Reduced-motion falls back to the stacked list.
      this.stackedMode = this.mqReducedMotion.matches;
      this.classList.toggle('bm-landing-rp--stacked', this.stackedMode);
      this.classList.remove('bm-landing-rp--past-end');
      window.removeEventListener('scroll', this.onScroll);

      if (this.stackedMode) {
        this.setStepOpacity(0, 0);
        return;
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.updateFromScroll();
    }

    updateFromScroll() {
      if (this.stackedMode) return;

      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;
      const { trackTop, trackBottom } = this.getTrackMetrics();
      const maxIndex = this.stepCount - 1;

      const isBeforeTrack = scrollY + viewportHeight <= trackTop;
      const isPastTrack = scrollY >= trackBottom;

      this.classList.toggle('bm-landing-rp--past-end', isPastTrack);

      if (isBeforeTrack || isPastTrack) {
        return;
      }

      const segmentHeight = viewportHeight * (this.segmentVh / 100);
      let scrollValue = (scrollY - trackTop) / segmentHeight;
      scrollValue = Math.max(0, Math.min(maxIndex, scrollValue));

      const progress = maxIndex > 0 ? scrollValue / maxIndex : 0;
      this.setStepOpacity(scrollValue, progress);
    }

    getStepOpacity(index, scrollValue) {
      const maxIndex = this.stepCount - 1;
      const clamped = Math.max(0, Math.min(maxIndex, scrollValue));

      if (maxIndex === 0) {
        return index === 0 ? 1 : 0;
      }

      if (clamped >= maxIndex) {
        return index === maxIndex ? 1 : 0;
      }

      const floor = Math.floor(clamped);
      const local = clamped - floor;
      const holdEnd = Math.max(0, Math.min(1, 1 - this.crossfadeRatio));
      const blend = holdEnd >= 1 ? 0 : local <= holdEnd ? 0 : (local - holdEnd) / (1 - holdEnd);

      if (index === floor) return 1 - blend;
      if (index === floor + 1) return blend;
      return 0;
    }

    setStepOpacity(scrollValue, progress) {
      const maxIndex = this.stepCount - 1;
      const activeIndex = Math.min(maxIndex, Math.round(scrollValue));

      this.activeIndex = activeIndex;

      this.steps.forEach((step, index) => {
        const opacity = this.getStepOpacity(index, scrollValue);
        const isVisible = opacity > 0.02;

        step.style.setProperty('--stick-opacity', String(opacity));
        step.style.setProperty('--stick-visibility', isVisible ? 'visible' : 'hidden');
        step.style.setProperty('--stick-layer', String(index));
        step.classList.toggle('is-active', opacity > 0.5);
        step.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      });

      if (this.progressFill) {
        const fillLength = this.railHeight * progress;
        this.progressFill.setAttribute('y2', String(fillLength));
      }

      // Mobile horizontal rail fills one segment per step (Figma: step 1 ≈ 1/6 → step 6 = full).
      if (this.mProgressFill) {
        const mFrac = this.stepCount > 0 ? Math.min(1, (scrollValue + 1) / this.stepCount) : 1;
        this.mProgressFill.style.width = `${(mFrac * 100).toFixed(2)}%`;
      }

      if (this.liveRegion) {
        const activeStep = this.steps[activeIndex];
        const title = activeStep?.querySelector('.bm-landing-rp__step-title');
        if (title) {
          this.liveRegion.textContent = title.textContent;
        }
      }
    }
  }

  customElements.define('bm-landing-ring-process', BmLandingRingProcess);
}
